import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Filter, X, RotateCcw } from "lucide-react";
import { AdvancedFilterCriteria } from "@/services/advancedChateauFilterService";
interface AdvancedFilterControlsProps {
  spaceOptions: Array<{
    value: string;
    label: string;
  }>;
  budgetOptions: Array<{
    value: string;
    label: string;
  }>;
  ageOptions: Array<{
    value: string;
    label: string;
  }>;
  themeOptions: Array<{
    value: string;
    label: string;
  }>;
  sizeOptions: Array<{
    value: string;
    label: string;
  }>;
  featureOptions: Array<{
    value: string;
    label: string;
  }>;
  criteria: AdvancedFilterCriteria;
  onCriteriaChange: (criteria: AdvancedFilterCriteria) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
  compact?: boolean;
  resultsCount?: number;
}
export const AdvancedFilterControls = ({
  spaceOptions,
  budgetOptions,
  ageOptions,
  themeOptions,
  sizeOptions,
  featureOptions,
  criteria,
  onCriteriaChange,
  onReset,
  hasActiveFilters: hasActiveFiltersProp,
  compact = false,
  resultsCount = 0
}: AdvancedFilterControlsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const updateCriteria = (key: keyof AdvancedFilterCriteria, value: any) => {
    onCriteriaChange({
      ...criteria,
      [key]: value
    });
  };
  const toggleFeature = (feature: string) => {
    const currentFeatures = criteria.features || [];
    const newFeatures = currentFeatures.includes(feature) ? currentFeatures.filter(f => f !== feature) : [...currentFeatures, feature];
    updateCriteria('features', newFeatures);
  };
  const resetFilters = () => {
    onReset();
  };
  const hasActiveFilters = hasActiveFiltersProp;
  const activeFiltersCount = Object.keys(criteria).filter(key => {
    const value = criteria[key as keyof AdvancedFilterCriteria];
    return value && (Array.isArray(value) ? value.length > 0 : value !== "");
  }).length;
  if (compact) {
    return <Card className="gradient-primary text-white shadow-playful">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-display font-bold">Filtres avancés</h3>
            <div className="flex items-center gap-2">
              {hasActiveFilters && <Badge variant="secondary" className="bg-white/20 text-white">
                  {activeFiltersCount} filtre{activeFiltersCount !== 1 ? 's' : ''}
                </Badge>}
              <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)} className="text-white hover:bg-white/10">
                <Filter className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {isExpanded && <div className="space-y-4">
              {/* Filtres de base */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Espace disponible</label>
                  <select className="w-full px-3 py-2 border border-white/20 rounded-md bg-white/10 text-white backdrop-blur-sm" value={criteria.space || ""} onChange={e => updateCriteria('space', e.target.value)}>
                    {spaceOptions.map(option => <option key={option.value} value={option.value} className="text-foreground bg-background">
                        {option.label}
                      </option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Budget</label>
                  <select className="w-full px-3 py-2 border border-white/20 rounded-md bg-white/10 text-white backdrop-blur-sm" value={criteria.budget || ""} onChange={e => updateCriteria('budget', e.target.value)}>
                    {budgetOptions.map(option => <option key={option.value} value={option.value} className="text-foreground bg-background">
                        {option.label}
                      </option>)}
                  </select>
                </div>
              </div>

              {/* Filtres avancés */}
              <Separator className="bg-white/20" />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Âge des enfants</label>
                  <select className="w-full px-3 py-2 border border-white/20 rounded-md bg-white/10 text-white backdrop-blur-sm" value={criteria.ageGroup || ""} onChange={e => updateCriteria('ageGroup', e.target.value)}>
                    {ageOptions.map(option => <option key={option.value} value={option.value} className="text-foreground bg-background">
                        {option.label}
                      </option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Thème</label>
                  <select className="w-full px-3 py-2 border border-white/20 rounded-md bg-white/10 text-white backdrop-blur-sm" value={criteria.theme || ""} onChange={e => updateCriteria('theme', e.target.value)}>
                    {themeOptions.map(option => <option key={option.value} value={option.value} className="text-foreground bg-background">
                        {option.label}
                      </option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Taille du château</label>
                  <select className="w-full px-3 py-2 border border-white/20 rounded-md bg-white/10 text-white backdrop-blur-sm" value={criteria.size || ""} onChange={e => updateCriteria('size', e.target.value)}>
                    {sizeOptions.map(option => <option key={option.value} value={option.value} className="text-foreground bg-background">
                        {option.label}
                      </option>)}
                  </select>
                </div>
              </div>

              {/* Caractéristiques */}
              <div>
                <label className="block text-sm font-medium mb-3">Caractéristiques souhaitées</label>
                <div className="flex flex-wrap gap-2">
                  {featureOptions.map(option => <label key={option.value} className="flex items-center">
                      <Checkbox checked={(criteria.features || []).includes(option.value)} onCheckedChange={() => toggleFeature(option.value)} className="mr-2" />
                      <span className="text-sm">{option.label}</span>
                    </label>)}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-white/20">
                <div className="text-sm opacity-90">
                  {resultsCount} château{resultsCount !== 1 ? 'x' : ''} trouvé{resultsCount !== 1 ? 's' : ''}
                </div>
                <Button variant="ghost" size="sm" onClick={resetFilters} disabled={!hasActiveFilters} className="text-white hover:bg-white/10">
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Réinitialiser
                </Button>
              </div>
            </div>}
        </CardContent>
      </Card>;
  }

  // Version complète (non-compact)
  return <Card className="shadow-gentle border-0">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Filter className="w-5 h-5 mr-2 text-primary" />
            Filtres de recherche
          </div>
          {hasActiveFilters && <Button variant="ghost" size="sm" onClick={resetFilters}>
              <X className="w-4 h-4 mr-1" />
              Effacer
            </Button>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 py-0 px-0 my-0">
        {/* Filtres de base */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Espace disponible</label>
            <select className="w-full px-3 py-2 border rounded-md" value={criteria.space || ""} onChange={e => updateCriteria('space', e.target.value)}>
              {spaceOptions.map(option => <option key={option.value} value={option.value}>
                  {option.label}
                </option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Budget</label>
            <select className="w-full px-3 py-2 border rounded-md" value={criteria.budget || ""} onChange={e => updateCriteria('budget', e.target.value)}>
              {budgetOptions.map(option => <option key={option.value} value={option.value}>
                  {option.label}
                </option>)}
            </select>
          </div>
        </div>

        <Separator />

        {/* Filtres avancés */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Âge des enfants</label>
            <select className="w-full px-3 py-2 border rounded-md" value={criteria.ageGroup || ""} onChange={e => updateCriteria('ageGroup', e.target.value)}>
              {ageOptions.map(option => <option key={option.value} value={option.value}>
                  {option.label}
                </option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Thème préféré</label>
            <select className="w-full px-3 py-2 border rounded-md" value={criteria.theme || ""} onChange={e => updateCriteria('theme', e.target.value)}>
              {themeOptions.map(option => <option key={option.value} value={option.value}>
                  {option.label}
                </option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Taille du château</label>
            <select className="w-full px-3 py-2 border rounded-md" value={criteria.size || ""} onChange={e => updateCriteria('size', e.target.value)}>
              {sizeOptions.map(option => <option key={option.value} value={option.value}>
                  {option.label}
                </option>)}
            </select>
          </div>
        </div>

        {/* Caractéristiques */}
        <div>
          <label className="block text-sm font-medium mb-3">Caractéristiques souhaitées</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {featureOptions.map(option => <label key={option.value} className="flex items-center space-x-2">
                <Checkbox checked={(criteria.features || []).includes(option.value)} onCheckedChange={() => toggleFeature(option.value)} />
                <span className="text-sm">{option.label}</span>
              </label>)}
          </div>
        </div>

        {/* Résultats */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {resultsCount} château{resultsCount !== 1 ? 'x' : ''} correspond{resultsCount === 1 ? '' : 'ent'} à vos critères
          </div>
          {hasActiveFilters && <Badge variant="secondary">
              {activeFiltersCount} filtre{activeFiltersCount !== 1 ? 's' : ''} actif{activeFiltersCount !== 1 ? 's' : ''}
            </Badge>}
        </div>
      </CardContent>
    </Card>;
};